import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DatasourceType } from '@selfai-platform/pipeline-common';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputSwitchModule } from 'primeng/inputswitch';
import { TooltipModule } from 'primeng/tooltip';
import { combineLatest, map, mergeMap, Observable } from 'rxjs';
import { DataSourceListDomainService, DataSourcePermissionService } from '../../../data-sources/services';
import { getMapIconsByType, getVisibility } from '../../../data-sources/utilities';
import { dataSourceListRoute, getDataSourceEditRoute } from '../../../utils';
import { DataSourceFormViewerComponent } from './data-source-form-viewer';

@Component({
  selector: 'selfai-platform-data-source-selector',
  standalone: true,
  imports: [
    CommonModule,
    DropdownModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    TooltipModule,
    RouterModule,
    MonacoEditorModule,
    FormsModule,
    InputSwitchModule,
    DataSourceFormViewerComponent,
  ],
  templateUrl: './data-source-selector.component.html',
  styleUrl: './data-source-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataSourceSelectorComponent implements OnInit {
  @Input({ required: true }) control: FormControl<string>;
  @Input() excludedDataSourceTypes: DatasourceType[] = [];
  @Input() showJsonView = false;
  @Input() showFormView = true;

  readonly editorOptions = {
    theme: 'vs',
    language: 'json',
    automaticLayout: true,
    minimap: { enabled: false },
    wordWrap: 'on',
    readOnly: true,
    scrollbar: {
      vertical: 'hidden',
      horizontal: 'hidden',
    },
  } as const;

  datasources$ = this.dataSourceListDomainService
    .getDataSourceList()
    .pipe(map((d) => d.filter((t) => !this.excludedDataSourceTypes.includes(t.params.datasourceType))));

  datasourcesLoading$ = this.dataSourceListDomainService.getDataSourceListLoading();
  canEditMap$: Observable<Map<string, boolean>> = this.mapEditPermissionsByDataSources();

  selectedDatasource: string | null;

  iconsByDataSourceType = getMapIconsByType();

  get selectedDatasourceJson$(): Observable<string> {
    return this.datasources$.pipe(
      map((d) => d.find((t) => t.id === this.control.value)),
      map((d) => (d ? JSON.stringify(d, null, '\t') : '')),
    );
  }

  constructor(
    private readonly dataSourceListDomainService: DataSourceListDomainService,
    private readonly dataSourcePermissionService: DataSourcePermissionService,
    private readonly translateService: TranslateService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.dataSourceListDomainService.loadDataSourceList();
  }

  onReloadDataSources(): void {
    this.dataSourceListDomainService.loadDataSourceList();
  }

  openDataSourceInNewTab(dataSourceId: string): void {
    this.openInNewTab(getDataSourceEditRoute(dataSourceId));
  }

  openDataSourceListInNewTab(): void {
    this.openInNewTab(dataSourceListRoute);
  }

  private openInNewTab(route: string[]): void {
    const url = this.router.serializeUrl(this.router.createUrlTree(route));
    window.open(url, '_blank', 'noopener');
  }

  private mapEditPermissionsByDataSources(): Observable<Map<string, boolean>> {
    return this.datasources$
      .pipe(
        mergeMap((datasources) =>
          combineLatest(
            datasources.map((datasource) =>
              this.dataSourcePermissionService
                .canUpdateSource(datasource.ownerId, getVisibility(datasource.params.visibility) === 'public')
                .pipe(map((canEdit) => ({ canEdit, dataSourceId: datasource.id }))),
            ),
          ),
        ),
      )
      .pipe(
        map((permissions) => {
          const permissionsMap = new Map<string, boolean>();
          permissions.forEach((permission) => permissionsMap.set(permission.dataSourceId, permission.canEdit));

          return permissionsMap;
        }),
      );
  }
}
